<template>
  <div class="panel panel-default programme-details">
    <h2>Programmes</h2>
    <span class="programme-access" v-if="selectedProgramme">
      Programme Access:
      <span>
        <b>
          <span v-if="!selectedProgramme.is_active" class="fas fa-archive" title="Programme is archived" aria-hidden="true"></span>
          <span v-if="selectedProgramme.is_frozen" class="frozen">Frozen</span>
          <span v-else>Granted</span>
        </b>
        <button v-if="canManageProgrammes" @click="$emit('freeze-click')" :class="selectedProgramme.is_frozen ? 'unfreeze' : 'freeze'">
          <span v-if="selectedProgramme.is_frozen">Unfreeze</span>
          <span v-else>Freeze</span>
          <i :class="selectedProgramme.is_frozen ? 'fa-fire-alt' : 'fa-snowflake'" class="fas" :title="`Click to ${selectedProgramme.is_frozen ? 'un' : ''}freeze student access to programme`" aria-hidden="true"></i>
        </button>
      </span>
    </span>
    <span class="learner-status" v-if="selectedProgramme">
      <span class="programme-detail-label">Learner Status:</span>
      <set-learner-status v-if="!selectedProgramme.learner_status_source"
                          :class="learnerStatusClass"
                          :options="learnerStatus"
                          :selected-programme="selectedProgramme"
                          :canUpdate="canManageProgrammes"
                          @new-data="(status) => $emit('assign-learner-status', status)"
      >
      </set-learner-status>
      <span v-else class="programme-detail-value" :class="learnerStatusClass">
        {{ selectedProgramme.learner_status }}
        <k-external-data-label
          class="programme-source-label"
          :label="selectedProgramme.learner_status_source"
        />
      </span>
    </span>
    <span class="graduation-date" v-if="selectedProgramme">
      <set-student-metadata
        v-if="!selectedProgramme.estimated_graduation_date_source"
        class="programme-detail-editor"
        :inline="true"
        assignedType="Graduation Date"
        :estimatedGraduationDate="selectedProgramme.estimated_graduation_date"
        :canUpdate="canManageProgrammes"
        @new-data="(date) => $emit('assign-graduation-date', date)"
      ></set-student-metadata>
      <span
        v-else
        class="graduation-date-pics"
        title="This is the EPA end date from the learner's ILR"
      >
        <span class="programme-detail-label">Graduation Date:</span>
        <span class="programme-detail-value">
          <b>{{ parseTimestamp(selectedProgramme.estimated_graduation_date, undefined, 'do MMM yyyy') }}</b>
          <k-external-data-label
            class="programme-source-label"
            :label="selectedProgramme.estimated_graduation_date_source"
          />
        </span>
      </span>
    </span>
    <span class="graduation-date" v-if="hasPlannedEndDate">
      <span class="graduation-date-pics">
        <span class="programme-detail-label">Planned End Date:</span>
        <span class="programme-detail-value">
          <b>{{ parseTimestamp(selectedProgramme.ilr_planned_end_date, undefined, 'do MMM yyyy') }}</b>
          <k-external-data-label
            class="programme-source-label"
            :label="selectedProgramme.ilr_planned_end_date_source"
          />
        </span>
      </span>
    </span>
    <a v-if="showAptemLink"
      class="aptem-link"
      target="_blank"
      :href="aptemLink"
    >
      Link to Aptem <i class="fas fa-external-link-alt" aria-hidden="true"></i>
    </a>
    <span v-if="currentProgrammeGroups && currentProgrammeGroups.length" class="programme-groups">
      <span>Programme Groups:</span>
      <span v-for="group in currentProgrammeGroups" :key="`${group}_${selectedProgramme.id}`" class="badge">{{ group }}</span>
    </span>
    <k-dropdown placeholder="Select Programme" :options="programmes" :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)"></k-dropdown>
    <button
      v-if="showManageIlrButton"
      class="btn btn-primary open-manage-ilr"
      :title="'Open the Manage ILR Summary page'"
      @click="$emit('open-manage-ilr')">
        Manage ILR Summary
        <i class="fas fa-external-link-alt" aria-hidden="true"></i>
    </button>
  </div>
</template>

<style scoped>
.programme-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
}

.programme-access b {
  color: var(--kate-type-success);
  margin-right: 10px;
}

.programme-access .frozen {
  color: var(--kate-disabled);
}

.programme-access button:hover {
  background-color: var(--kate-button-primary-hover);
}

.programme-access button.freeze {
  background-color: var(--kate-button-primary);
}

.programme-access button.unfreeze {
  background-color: var(--kate-success);
  color: var(--kate-type-dark);
}

.learner-status {
  display: flex;
  gap: 5px;
  padding-bottom: 5px;
}

.graduation-date-pics {
  display: flex;
  gap: 5px;
}

.programme-detail-label {
  flex: 0 0 120px;
}

.programme-detail-editor :deep(.update-options) {
  gap: 5px;
}

.programme-detail-editor :deep(.update-options > span:first-child) {
  flex: 0 0 120px;
}

.programme-detail-value {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
  min-width: 0;
}

.programme-source-label {
  font-family: hk-grotesk-light, sans-serif;
  font-size: 0.75rem;
  font-weight: normal;
  line-height: normal;
}

.learner-status .danger {
  color: var(--kate-type-danger);
}

.learner-status .success {
  color: var(--kate-type-success);
}
</style>

<script>
import KDropdown from '@base-components/k-dropdown.vue';
import KExternalDataLabel from '@base-components/k-external-data-label.vue';
import TimeMixin from '@mixins/time-mixins';
import SetStudentMetadata from './set-student-metadata.vue';
import SetLearnerStatus from './set-learner-status.vue';

export default {
  components: {
    'k-dropdown': KDropdown,
    'set-learner-status': SetLearnerStatus,
    'set-student-metadata': SetStudentMetadata,
    KExternalDataLabel,
  },

  mixins: [TimeMixin],

  props: {
    selectedProgramme: Object,
    programmes: {
      type: Array,
      required: true,
    },
    modelValue: Number,
    learnerStatus: {
      type: Array,
      required: true,
    },
  },

  emits: [
    'update:modelValue',
    'freeze-click',
    'assign-learner-status',
    'assign-graduation-date',
    'open-manage-ilr',
  ],

  computed: {
    canManageProgrammes() {
      return this.$permissions.hasPermission('manage_programmes');
    },

    aptemLink() {
      return this.selectedProgramme?.aptem_link;
    },

    showAptemLink() {
      return this.aptemLink && this.$permissions.hasPermission('view_external_data_source_labels');
    },

    currentProgrammeGroups() {
      return this.selectedProgramme?.programme_groups;
    },

    showManageIlrButton() {
      return this.$featureFlags.isEnabled('ilr-management-page')
        && this.$permissions.hasPermission('access_ilr_management');
    },

    hasPlannedEndDate() {
      return Boolean(this.selectedProgramme?.ilr_planned_end_date);
    },

    learnerStatusClass() {
      if (!this.selectedProgramme) {
        return '';
      }
      if (this.selectedProgramme.learner_status === 'Leaver' || this.selectedProgramme.learner_status === 'Non-starter') {
        return 'danger';
      }
      if (this.selectedProgramme.learner_status === 'Active' || this.selectedProgramme.learner_status === 'Graduated') {
        return 'success';
      }
      return '';
    },
  },
};
</script>
